import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateUserById } from '../../../../../app/store/slices/auth/authSlice';
import { errorToast, successToastTopRight } from '../../../../../utils/toasts/toasts';

export default function useAddressInfo() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [loading, setLoading] = useState(false);
    const { countries, allCities, allStates } = useSelector((state) => state.cities);

    const initialValues = {
        addressLine1: user?.address || '',
        addressLine2: user?.address_2 || '',
        pinCode: user?.pincode || '',
        city: allCities.find((city) => city.id === user?.invoice_city_id) || null,
        state: allStates.find((state) => state.id === user?.state_id) || null,
        country:
            countries.find((country) => country.id === user?.country_id) || countries[0] || null,
        isEditable: false
    };

    const validationSchema = Yup.object({
        addressLine1: Yup.string().when('isEditable', {
            is: true,
            then: (schema) => schema.required('Address Line 1 is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        addressLine2: Yup.string().notRequired(),
        city: Yup.mixed().when('isEditable', {
            is: true,
            then: (schema) => schema.required('City is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        state: Yup.mixed().when('isEditable', {
            is: true,
            then: (schema) => schema.required('State is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        pinCode: Yup.string().when('isEditable', {
            is: true,
            then: (schema) =>
                schema
                    .required('Pin Code is required')
                    .matches(/^\d{6}$/, 'Pin Code must be a 6-digit number'),
            otherwise: (schema) => schema.notRequired()
        }),
        country: Yup.mixed().when('isEditable', {
            is: true,
            then: (schema) => schema.required('Country is required'),
            otherwise: (schema) => schema.notRequired()
        })
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const payload = {
                    address: values.addressLine1,
                    address_2: values.addressLine2,
                    city_id: values.city.id || '',
                    state_id: values.state.id || '',
                    country_id: countries[0]?.id || ''
                };

                await dispatch(
                    updateUserById({
                        id: user.id,
                        data: payload
                    })
                ).unwrap();

                successToastTopRight('Address information updated successfully.');

                formik.setFieldValue('isEditable', false);
            } catch (error) {
                setLoading(false);
                formik.setFieldValue('isEditable', true, false);

                if (error?.errors && Array.isArray(error.errors)) {
                    const fieldErrors = {};
                    error.errors.forEach((errObj) => {
                        Object.entries(errObj).forEach(([field, message]) => {
                            // Map backend keys to Formik keys
                            const fieldMap = {
                                city_id: 'city',
                                state_id: 'state',
                                country_id: 'country',
                                pin_code: 'pinCode',
                                address: 'addressLine1',
                                address_2: 'addressLine2'
                            };

                            const formikField = fieldMap[field] || field;
                            fieldErrors[formikField] = message;
                        });
                    });
                    formik.setErrors(fieldErrors);
                }
            } finally {
                setLoading(false);
            }
        }
    });

    return {
        countries,
        formik,
        loading,
        allCities,
        allStates
    };
}
